import React, { FunctionComponent } from "react";
import styled from "styled-components";

import EnquiryClient from "@/client/EnquiryClient";
import Modal from "@/components/Modal";
import { CM, CXL } from "@/components/Typography";
import RxError from "@/components/ui/display/RxError";
import RxButton from "@/components/ui/inputs/RxButton";
import RxCheckbox from "@/components/ui/inputs/RxCheckbox";
import RxLink from "@/components/ui/inputs/RxLink";
import RxTextInput from "@/components/ui/inputs/RxTextInput";
import { getJoiErrorMessage } from "@/handlers/joiErrorHandler";
import { getQueryParams } from "@/handlers/router/queryHandler";
import { colours } from "@/utils";
import * as gtag from "@/utils/gtag";
import { useRouter } from "next/router";
import MarketingButton from "../marketing/MarketingButton";
import RxDropdownSelect from "@/components/ui/inputs/RxDropdownSelect";
import MembershipTypes from "@/types/marketing/MembershipTypes";
import MembershipInterests from "@/types/marketing/MembershipInterests";
import enquireSchema from "@/schemas/marketing/enquireSchema";

type SessionsHouseFormProps = {
  type: string;
  textColour?: string;
};

const memberships = [
  "AllBright@ Old Sessions House",
  "AllBright Accelerate",
  "AllBright Elevate",
  "Elevate to the Board",
  "AllBright Business",
];

const SessionsHouseForm: FunctionComponent<SessionsHouseFormProps> = (
  props
) => {
  const router = useRouter();
  const [isLoading, setIsLoading] = React.useState(false);
  const [membershipType, setMembershipType] = React.useState<
    (typeof memberships)[number]
  >("AllBright Townhouse");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [company, setCompany] = React.useState("");
  const [jobTitle, setJobTitle] = React.useState("");
  const [membershipInterest, setMembershipInterest] =
    React.useState<(typeof MembershipInterests)[number]>();
  const [otherInterest, setOtherInterest] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [acceptMarketing, setAcceptMarketing] = React.useState(false);
  const [currentErrors, setCurrentErrors] = React.useState([]);
  const [modalOpen, setModalOpen] = React.useState(false);

  const otherInterestSelected = membershipInterest === "Other";

  const checkErrorsForField = (field) => {
    return currentErrors.map((error) => error.type).includes(field);
  };

  const removeFieldFromErrors = (field) => {
    setCurrentErrors(currentErrors.filter((error) => error.type !== field));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMembershipType("AllBright Townhouse");
    if (isLoading) return;

    setIsLoading(true);
    setCurrentErrors([]);

    const newMembershipType = (
      membershipType === "AllBright@ Old Sessions House"
        ? "AllBright Townhouse"
        : membershipType
    ) as (typeof MembershipTypes)[number];

    const body = {
      membership_type: newMembershipType,
      first_name: firstName,
      last_name: lastName,
      email: email,
      job_title: jobTitle,
      job_company: company,
      membership_interest: membershipInterest,
      other_membership_interest: otherInterest,
      marketing_emails: acceptMarketing,
      query_params: getQueryParams(router),
    };

    try {
      await enquireSchema.validateAsync(body, { abortEarly: false });
    } catch (error) {
      setCurrentErrors(
        Object.keys(body)
          .map((key) => getJoiErrorMessage(key, error.details))
          .filter((message) => message !== null)
      );
      setIsLoading(false);
      return;
    }

    try {
      const client = new EnquiryClient();
      await client.enquireAboutMemberships(body);

      gtag.event({
        name: gtag.GA_EVENT.MARKETING_ENGAGEMENT,
        category: "Membership Enquiry",
        label: membershipType,
        value: 1,
      });
    } catch (err) {
      setCurrentErrors([
        err.response?.data?.errorMessages ||
          err?.message ||
          "Something went wrong",
      ]);
    }

    setModalOpen(true);
    setIsLoading(false);
  };

  const onCloseModalHandler = () => {
    resetForm();
    setModalOpen(false);
  };

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setCompany("");
    setJobTitle("");
    setEmail("");
    setAcceptMarketing(false);
  };

  return (
    <S.Form>
      {modalOpen && (
        <>
          <Modal onClose={() => onCloseModalHandler()}>
            <S.ModalContent>
              <CXL weight="700" colour={colours.marketingProgrammesNavy}>
                Success!
              </CXL>
              <CXL weight="500">
                Your details have been successfully submitted, you will receive
                further communication from us shortly.
              </CXL>
              <RxButton
                buttonText="Okay"
                background={colours.marketingProgrammesNavy}
                onClick={() => setModalOpen(false)}
              />
            </S.ModalContent>
          </Modal>
        </>
      )}
      <S.TwoFieldsContainer>
        <RxTextInput
          labelText="First name *"
          name="first_name"
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
            removeFieldFromErrors("firstName");
          }}
          error={checkErrorsForField("firstName")}
        />
        <RxTextInput
          labelText="Last name *"
          name="last_name"
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
            removeFieldFromErrors("lastName");
          }}
          error={checkErrorsForField("lastName")}
        />
      </S.TwoFieldsContainer>
      <RxTextInput
        labelText="Email *"
        name="email"
        type="email"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          removeFieldFromErrors("email");
        }}
        error={checkErrorsForField("email")}
      />
      <RxTextInput
        labelText="Job title *"
        name="job_title"
        value={jobTitle}
        onChange={(e) => {
          setJobTitle(e.target.value);
          removeFieldFromErrors("jobTitle");
        }}
        error={checkErrorsForField("jobTitle")}
      />
      <RxTextInput
        labelText="Company *"
        name="job_company"
        value={company}
        onChange={(e) => {
          setCompany(e.target.value);
          removeFieldFromErrors("company");
        }}
        error={checkErrorsForField("company")}
      />
      <RxDropdownSelect
        name="membership_interest"
        labelText="Why are you intersted in this membership? *"
        options={[...MembershipInterests]}
        selected={membershipInterest}
        onChange={(e) =>
          setMembershipInterest(
            e.target.value as (typeof MembershipInterests)[number]
          )
        }
      />
      {otherInterestSelected && (
        <RxTextInput
          name="other_membership_interest"
          labelText="Please state your interest here*"
          value={otherInterest}
          onChange={(e) => setOtherInterest(e.target.value)}
        />
      )}
      <RxCheckbox
        key="acceptMarketing"
        name="acceptMarketing"
        labelSize="small"
        checkColour={colours.marketingProgrammesNavy}
        labelText={
          "Hear about our events, promotions and products. Your data is secure and you can unsubscribe anytime."
        }
        labelWeight="500"
        onChange={() => {
          setAcceptMarketing(!acceptMarketing);
        }}
        isChecked={acceptMarketing}
      />
      {currentErrors.length > 0 && (
        <S.ErrorsContainer>
          {currentErrors.map((error) => (
            <RxError key={error.message} message={error.message} />
          ))}
        </S.ErrorsContainer>
      )}
      <S.ButtonContainer>
        <MarketingButton
          buttonText="Submit form"
          colour={colours.marketingProgrammesNavy}
          disabled={isLoading}
          name="submit_button"
          textColour={colours.marketingProgrammesNavy}
          type="button"
          onClick={handleSubmit}
        />
      </S.ButtonContainer>
      <CM colour={props.textColour} weight="500">
        Submitting this form means you agree to our{" "}
        <RxLink
          href="/privacy"
          textColour={colours.marketingProgrammesNavy}
          textLink
        >
          Privacy Policy.
        </RxLink>
      </CM>
    </S.Form>
  );
};

const S = () => {};

S.Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

S.ModalContent = styled.div`
  padding: 3% 10% 8% 10%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

S.TwoFieldsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
`;

S.RadioContainer = styled.div`
  margin: 1rem 0 0 0;
`;

S.CheckboxesContainer = styled.div`
  margin: 0.5rem 0 0 0;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

S.ErrorsContainer = styled.div`
  border-radius: 0.5rem;
  padding: 3% 10%;
  background: ${colours.errorYellow};
  transition: all 0.2s ease-out;
`;

S.ButtonContainer = styled.div`
  margin: 1rem 0;
`;

export default SessionsHouseForm;
